/**
 * Barra de gravação de áudio no composer (timer, ondas, pause/preview/cancel).
 * Pausado: [lixeira · play · ondas · tempo · mic] — sem bolinha vermelha.
 * Play atrás das ondas; lixeira perto do play para descartar.
 */
import React, { useCallback, useMemo, useRef } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';

interface ChatRecordingBarProps {
    isRecording: boolean;
    isPaused: boolean;
    isPreviewPlaying: boolean;
    canResumePreview: boolean;
    previewProgress: number;
    recordingTime: number;
    formatTime: (time: number) => string;
    cancelRecording: () => void;
    pauseRecording: () => void;
    resumeRecording: () => void;
    togglePreviewPlayback: () => void;
    seekPreview: (ratio: number) => void;
}

const WAVE_COUNT = 20;
const THUMB_PX = 14; // size-3.5

const ICON_BTN =
    'inline-flex size-9 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const ICON_BTN_MUTED = `${ICON_BTN} text-secondary hover:bg-primary-subtle hover:text-primary active:bg-primary-subtle`;
const ICON_BTN_PRIMARY = `${ICON_BTN} text-primary hover:bg-primary-subtle hover:text-primary active:bg-primary-subtle`;
const ICON_BTN_DANGER = `${ICON_BTN} text-secondary hover:bg-danger-bg hover:text-error active:bg-danger-bg`;

const AudioWaveScrubber: React.FC<{
    isPaused: boolean;
    isPreviewPlaying: boolean;
    progress: number;
    seekable: boolean;
    onSeek: (ratio: number) => void;
}> = ({ isPaused, isPreviewPlaying, progress, seekable, onSeek }) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const draggingRef = useRef(false);

    const heights = useMemo(
        () => Array.from({ length: WAVE_COUNT }, () => Math.random() * 16 + 8),
        []
    );

    const animate = !isPaused || isPreviewPlaying;
    const ratio = Math.min(1, Math.max(0, progress));

    const ratioFromClientX = useCallback((clientX: number) => {
        const el = trackRef.current;
        if (!el) return 0;
        const rect = el.getBoundingClientRect();
        if (rect.width <= 0) return 0;
        return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    }, []);

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!seekable) return;
        e.preventDefault();
        draggingRef.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        onSeek(ratioFromClientX(e.clientX));
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!seekable || !draggingRef.current) return;
        onSeek(ratioFromClientX(e.clientX));
    };

    const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
            // ignore
        }
    };

    return (
        <div
            ref={trackRef}
            role={seekable ? 'slider' : undefined}
            aria-label={seekable ? 'Posição do áudio' : undefined}
            aria-valuemin={seekable ? 0 : undefined}
            aria-valuemax={seekable ? 100 : undefined}
            aria-valuenow={seekable ? Math.round(ratio * 100) : undefined}
            tabIndex={seekable ? 0 : undefined}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onKeyDown={
                seekable
                    ? (e) => {
                          if (e.key === 'ArrowLeft') {
                              e.preventDefault();
                              onSeek(ratio - 0.05);
                          } else if (e.key === 'ArrowRight') {
                              e.preventDefault();
                              onSeek(ratio + 0.05);
                          }
                      }
                    : undefined
            }
            className={`relative inline-flex h-8 w-fit shrink-0 items-center ${seekable ? 'cursor-pointer touch-none' : ''}`}
        >
            <div className="flex h-6 items-center gap-[3px]">
                {heights.map((height, i) => {
                    const filled = seekable && (i + 0.5) / WAVE_COUNT <= ratio;
                    return (
                        <div
                            key={i}
                            className={`chat-audio-wave-bar w-[3px] shrink-0 rounded-full ${
                                filled ? 'bg-primary' : 'bg-primary/40'
                            } ${animate ? 'chat-audio-wave-bar--on' : ''}`}
                            style={{
                                height: `${height}px`,
                                opacity: animate || filled ? 1 : 0.5,
                                animationDelay: `${i * 0.05}s`,
                            }}
                        />
                    );
                })}
            </div>

            {seekable ? (
                <div
                    className="pointer-events-none absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-primary shadow-sm ring-2 ring-card"
                    style={{ left: `calc(${ratio} * (100% - ${THUMB_PX}px))` }}
                    aria-hidden
                />
            ) : null}

            <style>{`
                @keyframes audioWave {
                    0%, 100% { transform: scaleY(0.5); }
                    50% { transform: scaleY(1); }
                }
                .chat-audio-wave-bar {
                    animation: none 0.5s ease-in-out infinite;
                }
                .chat-audio-wave-bar--on {
                    animation-name: audioWave;
                }
            `}</style>
        </div>
    );
};

export const ChatRecordingBar: React.FC<ChatRecordingBarProps> = ({
    isRecording,
    isPaused,
    isPreviewPlaying,
    canResumePreview,
    previewProgress,
    recordingTime,
    formatTime,
    cancelRecording,
    pauseRecording,
    resumeRecording,
    togglePreviewPlayback,
    seekPreview,
}) => {
    if (!isRecording) return null;

    const previewLabel = isPreviewPlaying
        ? 'Pausar áudio'
        : canResumePreview
          ? 'Continuar de onde parou'
          : 'Ouvir áudio';

    return (
        <div
            className="flex min-w-0 flex-1 items-center justify-end gap-1"
            role="group"
            aria-label="Gravação de áudio"
        >
            <button
                type="button"
                onClick={cancelRecording}
                className={ICON_BTN_DANGER}
                title="Descartar áudio"
                aria-label="Descartar áudio"
            >
                <AppIcon name="trash" className="size-5" aria-hidden />
            </button>

            {isPaused ? (
                <button
                    type="button"
                    onClick={togglePreviewPlayback}
                    className={ICON_BTN_PRIMARY}
                    title={previewLabel}
                    aria-label={previewLabel}
                    aria-pressed={isPreviewPlaying}
                >
                    <AppIcon
                        name={isPreviewPlaying ? 'pause' : 'play'}
                        className="size-5 fill-current"
                        aria-hidden
                    />
                </button>
            ) : null}

            <AudioWaveScrubber
                isPaused={isPaused}
                isPreviewPlaying={isPreviewPlaying}
                progress={previewProgress}
                seekable={isPaused}
                onSeek={seekPreview}
            />

            <time
                className="min-w-11 shrink-0 font-mono text-sm font-normal tabular-nums text-secondary"
                aria-live="polite"
            >
                {formatTime(recordingTime)}
            </time>

            <button
                type="button"
                onClick={isPaused ? resumeRecording : pauseRecording}
                className={ICON_BTN_MUTED}
                title={isPaused ? 'Retomar gravação' : 'Pausar gravação'}
                aria-label={isPaused ? 'Retomar gravação' : 'Pausar gravação'}
            >
                <AppIcon name={isPaused ? 'mic' : 'pause'} className="size-5" aria-hidden />
            </button>
        </div>
    );
};
